import {
  CLINICIAN_ROLES,
  IAdvanceSearchFilter,
  INearbyPatient,
} from "@home-health-notify/domain";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { AdvanceSearchMapWrapper } from "./AdvanceSearchMap.style";
import {
  DEFAULT_ZOOM,
  fitCircleOnMap,
  getNearbyPatientsLocationData,
  mapElement,
  mapID,
  MARKER,
  PatientsLocationDataType,
} from "./AdvanceMapHelper";
import { Map, useMap } from "@vis.gl/react-google-maps";
import config from "../../../../utils/Config";
import { MarkerWithInfoWindow as NearbyMarkerWithInfoWindow } from "./MarkerWithInfoWindow";
import { LocationPinMarker } from "./LocationPinMarker";
import CustomCircle from "./CustomCircle";
import { convertMilesToMeters, DISTANCE_IN_MILES } from "hhn-commons";

interface AdvanceSearchMapProps {
  nearbyPatients: INearbyPatient[] | undefined;
  advanceSearchFilters: IAdvanceSearchFilter;
  isLoading: boolean;
  centerPinContent?: ReactNode;
  defaultZoom?: number;
  selectedDistance: DISTANCE_IN_MILES;
  selectedDiscipline?: CLINICIAN_ROLES;
}

const AdvanceSearchMap = ({
  nearbyPatients,
  advanceSearchFilters,
  isLoading,
  centerPinContent,
  defaultZoom,
  selectedDistance,
  selectedDiscipline,
}: AdvanceSearchMapProps) => {
  const map = useMap(mapID);
  const [patientsLocationData, setPatientsLocationData] =
    useState<PatientsLocationDataType>({});
  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [circleRadius, setCircleRadius] = useState<number>(
    convertMilesToMeters(selectedDistance),
  );
  const position: google.maps.LatLngLiteral = useMemo(() => {
    return {
      lat: advanceSearchFilters.lat,
      lng: advanceSearchFilters.lng,
    };
  }, [advanceSearchFilters]);

  useEffect(() => {
    if (nearbyPatients && nearbyPatients?.length >= 0 && !isLoading && map) {
      const nearbyPatientsLocationData =
        getNearbyPatientsLocationData(nearbyPatients);
      setPatientsLocationData(nearbyPatientsLocationData);
      fitCircleOnMap(map, selectedDistance, position, setCircleRadius);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearbyPatients, advanceSearchFilters, map, isLoading]);
  return (
    <AdvanceSearchMapWrapper
      id={mapElement}
      data-testid="advanced-search-map-container"
    >
      <Map
        id={mapID}
        style={{ width: "100%", height: "100%" }}
        defaultCenter={position}
        defaultZoom={defaultZoom ?? DEFAULT_ZOOM}
        disableDefaultUI={true}
        mapId={config.googleMapId}
        fullscreenControl={false}
        mapTypeControl={false}
        zoomControl={false}
        scrollwheel={false}
        disableDoubleClickZoom
        gestureHandling="none"
      >
        {centerPinContent && (
          <LocationPinMarker
            position={position}
            showInfoWindow={() => setActiveMarker(MARKER.PATIENT_ADDRESS)}
            closeInfoWindow={() => setActiveMarker(null)}
            isActive={activeMarker === MARKER.PATIENT_ADDRESS}
          >
            {centerPinContent}
          </LocationPinMarker>
        )}
        {Object.keys(patientsLocationData).map(coordinates => {
          const patients: INearbyPatient[] = patientsLocationData[coordinates];
          const latLng = coordinates.split(",");
          const position = { lat: +latLng[0], lng: +latLng[1] };
          return (
            <NearbyMarkerWithInfoWindow
              key={coordinates}
              position={position}
              patients={patients}
              showInfoWindow={() => setActiveMarker(coordinates)}
              closeInfoWindow={() => setActiveMarker(null)}
              isActive={activeMarker === coordinates}
              selectedDiscipline={selectedDiscipline}
            />
          );
        })}
        <CustomCircle center={position} radius={circleRadius} map={map} />
      </Map>
    </AdvanceSearchMapWrapper>
  );
};

export default AdvanceSearchMap;
